<script lang="ts">
    import { blur } from "svelte/transition";
    import { onDestroy, onMount } from "svelte";
    import { iframeListener } from "../../Api/IframeListener";
    import { modalIframeStore, modalVisibilityStore } from "../../Stores/ModalStore";
    import { isMediaBreakpointUp } from "../../Utils/BreakpointsUtils";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import FullScreenIcon from "../Icons/FullScreenIcon.svelte";
    import FullScreenIconMeta from "../Icons/meta/FullscreenIcon.svelte";
    import XIcon from "../Icons/XIcon.svelte";
    import ButtonClose from "../Input/ButtonClose.svelte";
    import { focusTrap } from "../../Utils/svelte-focus-trap";

    let modalIframe: HTMLIFrameElement | undefined = $state();
    let mainModal: HTMLDivElement;

    let isFullScreened = $state(false);

    function close() {
        modalVisibilityStore.set(false);
        if ($modalIframeStore != undefined) {
            iframeListener.sendModalCloseTriggered($modalIframeStore);
        }
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            // Only close if closable is undefined or true (not false)
            if ($modalIframeStore?.closable == undefined || $modalIframeStore?.closable == true) {
                close();
            }
        }
    }

    onMount(() => {
        resizeObserver.observe(mainModal);
        if ($modalIframeStore?.allowApi && modalIframe) {
            iframeListener.registerIframe(modalIframe);
        }
        // Note: the fullscreen functionality is not implemented yet
        /*if ($modalIframeStore?.position == "center") {
            isFullScreened = true;
        }*/
    });

    onDestroy(() => {
        // Note: we are running unregisterIframe every time and not only when allowApi is true,
        // because of a possible race condition where the $modalIframeStore store is emptied before onDestroy is called,
        // which would lead to an error in unregisterIframe.
        //if ($modalIframeStore?.allowApi) {
        if (modalIframe) {
            iframeListener.unregisterIframe(modalIframe);
        }
        //}
    });

    let modalUrl = $modalIframeStore
        ? new URL($modalIframeStore.src, gameManager.currentStartedRoom.mapUrl).toString()
        : undefined;

    let isMobile = $state(isMediaBreakpointUp("md"));
    const resizeObserver = new ResizeObserver(() => {
        isMobile = isMediaBreakpointUp("md");
    });
</script>

<svelte:window onkeydown={onKeyDown} />

<div
    data-testid="apps-modal"
    class="menu-container fixed h-dvh w-dvw z-2000 pointer-events-auto top-0 transition-all {isMobile
        ? 'mobile'
        : $modalIframeStore?.position} {isFullScreened ? 'fullscreened' : ''}"
    bind:this={mainModal}
    use:focusTrap
    role="dialog"
    aria-modal="true"
    aria-label={$modalIframeStore?.title || "Диалоговое окно"}
>
    <div
        class="w-full h-full bg-mlight/80 shadow-light rounded-md2 backdrop-blur flex flex-col"
        transition:blur={{ amount: 10, duration: 250 }}
    >
        <div
            class={`flex items-center content-center bg-mlight/80 backdrop-blur ${
                !(isFullScreened || isMobile) && "hidden"
            }
             p-2 space-x-0 @lg/main-layout:space-x-2 rounded-md2 z-50 duration-300 justify-between
                ${
                    isFullScreened || isMobile
                        ? "top-4 right-4"
                        : `${
                              $modalIframeStore?.position == "center" || $modalIframeStore?.position == "left"
                                  ? "gap-1 top-0 -right-20 flex-row"
                                  : "gap-1 top-0 -left-20 flex-row"
                          }`
                }
            `}
        >
            {#if modalUrl != undefined}
                {#if $modalIframeStore?.allowFullScreen}
                    <button
                        class="btn btn-light btn-sm"
                        onclick={() => (isFullScreened = !isFullScreened)}
                        aria-label={isFullScreened ? "Выйти из полноэкранного режима" : "Полноэкранный режим"}
                    >
                        {#if isFullScreened}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="icon icon-tabler icon-tabler-arrows-minimize"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="#1B2A40"
                                fill="none"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M5 9l4 0l0 -4" />
                                <path d="M3 3l6 6" />
                                <path d="M5 15l4 0l0 4" />
                                <path d="M3 21l6 -6" />
                                <path d="M19 9l-4 0l0 -4" />
                                <path d="M15 9l6 -6" />
                                <path d="M19 15l-4 0l0 4" />
                                <path d="M15 15l6 6" />
                            </svg>
                        {:else}
                            <FullScreenIcon strokeColor="#1B2A40" />
                        {/if}
                    </button>
                {/if}
            {/if}
            {#if $modalIframeStore?.closable == undefined || $modalIframeStore?.closable == true}
                <button
                    onclick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        close();
                    }}
                    class="btn btn-danger m-0 btn-sm"
                    style={isFullScreened == true ? "" : "margin: 0px;"}
                    data-testid="close-modal-button"
                    aria-label="Закрыть"
                >
                    <XIcon />
                </button>
            {/if}
        </div>
        {#if modalUrl != undefined}
            <div class="grow">
                <iframe
                    id="modalIframe"
                    bind:this={modalIframe}
                    height="100%"
                    width="100%"
                    allow={$modalIframeStore?.allow}
                    title={$modalIframeStore?.title}
                    src={modalUrl}
                    class="border-0 relative z-40"
                    allowtransparency
                    style="color-scheme: auto"
                ></iframe>
            </div>
        {/if}
    </div>
    <div
        class="absolute flex flex-col gap-3
        {$modalIframeStore?.position == 'center' ? 'top-0' : 'top-6'}
        {$modalIframeStore?.position == 'right' ? 'left-[-60px]' : 'right-[-60px]'}"
    >
        <ButtonClose onclick={close} />
        <button
            type="button"
            onclick={() => (isFullScreened = !isFullScreened)}
            aria-label={isFullScreened ? "Выйти из полноэкранного режима" : "Полноэкранный режим"}
            class="p-0 flex items-center justify-center shadow-light backdrop-blur close-window transition-allbg-mlight/80
                group aspect-square rounded-full bg-mlight/80 hover:bg-mlight-darker hover:shadow-light-hover"
        >
            <FullScreenIconMeta />
        </button>
    </div>
</div>

<style>
    .menu-container {
        &.mobile {
            width: 100% !important;
            height: 100% !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
        }
        &.right:not(.fullscreened),
        &.left:not(.fullscreened) {
            width: 33%;
        }
        &.right {
            right: 0;
        }
        &.left {
            left: 0;
        }
        &.center:not(.fullscreened) {
            width: 75%;
            height: 75%;
            left: 0;
            right: 0;
            top: 12.5%;
            margin-right: auto;
            margin-left: auto;
            @media (max-width: 1800px) {
                width: 70%;
                height: 70%;
                top: 15%;
            }
            @media (max-width: 1400px) {
                width: 65%;
                height: 65%;
                top: 17.5%;
            }
            @media (max-width: 1100px) {
                width: 60%;
                height: 60%;
                top: 19%;
            }
        }
    }
</style>
